import { useLayoutEffect } from 'react';
import { Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from './store/authStore';

// Pages
import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import RestoreAccount from './pages/RestoreAccount';
import VerifyEmail from './pages/VerifyEmail';
import AcceptInvitation from './pages/AcceptInvitation';
import Dashboard from './pages/Dashboard';
import SharedAccount from './pages/SharedAccount';
import Settings from './pages/Settings';
import Profile from './pages/Profile';
import PersonalBudget from './pages/PersonalBudget';
import Archives from './pages/Archives';
import Admin from './pages/Admin';
import Subscription from './pages/Subscription';
import SearchPage from './pages/Search';
import NotFound from './pages/NotFound';
import FollowUpResponse from './pages/FollowUpResponse';
import Unsubscribe from './pages/Unsubscribe';
import FeatureDashboard from './pages/features/FeatureDashboard';
import FeatureOperations from './pages/features/FeatureOperations';
import FeatureSharing from './pages/features/FeatureSharing';
import FeatureBudgets from './pages/features/FeatureBudgets';
import Contact from './pages/Contact';
import Pricing from './pages/Pricing';
import LegalNotice from './pages/LegalNotice';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';

// Layouts
import DashboardLayout from './components/layout/DashboardLayout';

// Components
import CookieBanner from './components/common/CookieBanner';
import GlobalNotification from './components/common/GlobalNotification';
import ScrollToTop from './components/common/ScrollToTop';

// Route protégée (vérifie authentification ET email vérifié)
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user } = useAuthStore();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Rediriger vers la vérification si l'email n'est pas vérifié
  if (user && !user.emailVerified) {
    return <Navigate to={`/verify-email?email=${encodeURIComponent(user.email)}`} replace />;
  }

  return <>{children}</>;
}

// Route admin (vérifie que l'utilisateur est admin)
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user } = useAuthStore();

  // Si l'utilisateur n'est pas admin, afficher la page 404
  if (user?.role !== 'admin') {
    return <NotFound />;
  }

  return <>{children}</>;
}

// Route publique (redirige si déjà connecté avec email vérifié)
// Prend en compte le returnUrl pour rediriger vers la bonne page
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user } = useAuthStore();
  const [searchParams] = useSearchParams();
  const returnUrl = searchParams.get('returnUrl');

  if (isAuthenticated && user?.emailVerified) {
    // Rediriger vers returnUrl si présent, sinon vers dashboard
    return <Navigate to={returnUrl || '/dashboard'} replace />;
  }

  return <>{children}</>;
}

function App() {
  // Révèle #root une fois le vrai contenu monté : lève le masquage anti-flash
  // posé par le boot guard dans index.html (voir commentaire dans index.html).
  // useLayoutEffect s'exécute après le commit DOM mais avant la peinture,
  // donc aucun flash de la page d'accueil ni écran blanc.
  useLayoutEffect(() => {
    document.documentElement.classList.remove('spa-boot');
  }, []);

  return (
    <>
      <ScrollToTop />
      <Routes>
        {/* Routes publiques */}
        <Route path="/" element={<Landing />} />
        <Route
          path="/login"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />
        <Route
          path="/register"
          element={
            <PublicRoute>
              <Register />
            </PublicRoute>
          }
        />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/restore-account" element={<RestoreAccount />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/accept-invitation" element={<AcceptInvitation />} />
        <Route path="/follow-up/:token/:response" element={<FollowUpResponse />} />
        <Route path="/unsubscribe/:token" element={<Unsubscribe />} />

        {/* Pages fonctionnalités */}
        <Route path="/fonctionnalites/dashboard" element={<FeatureDashboard />} />
        <Route path="/fonctionnalites/operations" element={<FeatureOperations />} />
        <Route path="/fonctionnalites/partage" element={<FeatureSharing />} />
        <Route path="/fonctionnalites/budgets" element={<FeatureBudgets />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/tarifs" element={<Pricing />} />

        {/* Pages légales */}
        <Route path="/mentions-legales" element={<LegalNotice />} />
        <Route path="/politique-confidentialite" element={<PrivacyPolicy />} />
        <Route path="/conditions-utilisation" element={<TermsOfService />} />

        {/* Routes protégées (Dashboard) */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Dashboard />} />
          <Route path="compte-commun" element={<SharedAccount />} />
          <Route path="reglages" element={<Settings />} />
          <Route path="mon-budget" element={<PersonalBudget />} />
          <Route path="recherche" element={<SearchPage />} />
          <Route path="profil" element={<Profile />} />
          <Route path="elements-supprimes" element={<Archives />} />
          <Route path="abonnement" element={<Subscription />} />
          <Route
            path="admin"
            element={
              <AdminRoute>
                <Admin />
              </AdminRoute>
            }
          />
          {/* Redirections des anciennes routes */}
          <Route path="revenus" element={<Navigate to="/dashboard" replace />} />
          <Route path="depenses" element={<Navigate to="/dashboard/compte-commun" replace />} />
          {/* 404 dans le dashboard */}
          <Route path="*" element={<NotFound />} />
        </Route>

        {/* 404 global */}
        <Route path="*" element={<NotFound />} />
      </Routes>

      {/* Bandeau cookies RGPD */}
      <CookieBanner />

      {/* Notifications globales (rate limiting, etc.) */}
      <GlobalNotification />
    </>
  );
}

export default App;
